import { useCallback } from 'react';

import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { resolvableServiceUsageForm2ApiMapper } from '../../ResolvableServiceUsage/mappers/resolvable-service-usage.form2api.mapper';
import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';
import type { ResolvableServiceUsageFormTab } from '../../ResolvableServiceUsage/enums/tab.enum';
import type { ResolvableServiceFormTab } from '../../ResolvableService/enums/tab.enum';

import type { CreateMutation } from '../types/props.type';

interface Params {
  readonly directMutation?: CreateMutation;
  readonly handleCreateSuccess: () => void;
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
}

interface FormikArrayActions {
  readonly createInForm: (
    value: Readonly<ResolvableServiceUsageForm_ResolvableServiceUsage>,
  ) => void;
}

export const useCreateResolvableServiceUsageSubmit = ({
  directMutation,
  handleCreateSuccess,
  serviceUsagesGroupId,
  serviceUserId,
}: Readonly<Params>) => {
  const handleSubmitFactory = useCallback(
    ({ createInForm }: Readonly<FormikArrayActions>) =>
      ({
        values,
        memos,
        formikHelpers: { setSubmitting },
        setWizardError,
      }: Readonly<
        WizardSubmitHandlerParams<
          ResolvableServiceUsageForm_ResolvableServiceUsage,
          ResolvableServiceUsageFormTab | ResolvableServiceFormTab
        >
      >) => {
        setWizardError(undefined);

        const onError = (error: unknown) => {
          setWizardError({ messages: userFriendlyErrors(error as BackError) });
          setSubmitting(false);
        };

        const onSuccess = () => {
          createInForm(values);

          handleCreateSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              serviceUsage: resolvableServiceUsageForm2ApiMapper(values),
              serviceUsagesGroupId,
              serviceUserId,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [handleCreateSuccess, serviceUsagesGroupId, serviceUserId, directMutation],
  );

  return { handleSubmitFactory };
};
